/**
 * `IDuplicationModel` — port of Unity
 * `Models/ProjectModel/DuplicationModel.cs`.
 *
 * The clipboard here is Campfire's own, NOT the OS clipboard: Unity holds a
 * `GameObject[]` buffer and a `Vector3?` position in memory, so copying a
 * model and then pressing ⌘V in a text field does nothing, and copying text
 * elsewhere never clobbers a copied model. The web port keeps that contract —
 * the buffer is ids, not serialized geometry.
 *
 * Material copy/paste (`CopyMaterial` / `PasteMaterial`) is the one member of
 * Unity's interface not ported here; it belongs with the material inspector,
 * which owns the slot the paste would write.
 */

import type { Emitter } from "@core/events/emitter";

/** What a paste or duplicate created: new model entities, and new cards (tile ids). */
export interface DuplicationResult {
  readonly entityIds: ReadonlyArray<string>;
  readonly tileIds: ReadonlyArray<string>;
}

export interface IDuplicationModel {
  /** Fires when the entity buffer or the copied position changes. */
  readonly onChanged: Emitter<void>;

  /** Unity `CanPaste`: the buffer still resolves to at least one live entity. */
  readonly canPaste: boolean;
  /** Unity `CanPastePosition`: a position has been copied this session. */
  readonly canPastePosition: boolean;

  /**
   * Unity `CanCopyOrDuplicate`: every target must be a ROOT entity or a card.
   * A part cannot be copied or duplicated on its own — it has no independent
   * existence in the scene.
   */
  canCopyOrDuplicate(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean;

  /** Unity `CopyGameObjects`. Replaces the buffer; no-op for non-roots. */
  copyEntities(sceneId: string, snapshotIds: ReadonlyArray<string>): void;

  /**
   * Unity `PasteGameObjects` → `DuplicateGameObjects(buffer)`: clones the
   * buffered entities and cards into the ACTIVE scene, on top of their source
   * pose. Objects deleted since the copy are skipped.
   */
  pasteEntitiesAsync(signal?: AbortSignal): Promise<DuplicationResult>;

  /** Unity `DuplicateGameObjects` — models and cards alike. */
  duplicateEntitiesAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<DuplicationResult>;

  /**
   * Unity `CopyPosition` — the selected snapshot's local position, or a card's
   * own position (the same scene frame as a root's).
   *
   * Unity copies the INSPECTOR position (origin-relative) and converts back to
   * local on paste (`SelectedGameObjectModel.Position`); the web copies and
   * writes the local value on both legs. Identical whenever the copy and the
   * paste share a parent — the case the single-selection gate all but forces —
   * and it differs only by the origin offset between two different models.
   */
  copyPosition(sceneId: string, snapshotId: string): void;

  /** Unity `PastePosition` — applies the copied position, keeping rot/scale. */
  pastePositionAsync(
    sceneId: string,
    snapshotId: string,
    signal?: AbortSignal,
  ): Promise<void>;

  /** Drop the buffer (scene teardown / project close). */
  clear(): void;
}

export const DUPLICATION_MODEL = Symbol.for("Campfire.DuplicationModel");
